import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TeacherPick = ({ teachers, value, onChange, testid }) => (
  <select className="h-8 rounded border border-slate-300 bg-white px-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    <option value="">Choose teacher…</option>
    {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
  </select>
);

function Row({ s, kind, teachers, onDone }) {
  const pre = (kind === "broken" ? s.teachers : s.suggested)?.[0]?.id || "";
  const [tid, setTid] = useState(pre);
  const [busy, setBusy] = useState(false);
  const fix = async () => {
    if (!tid) { toast.error("Choose a teacher first"); return; }
    setBusy(true);
    try {
      if (kind === "broken") await api.post(`/admin/students/${s.id}/restore-merged`, { teacher_ids: [tid] });
      else await api.put(`/admin/students/${s.id}/teachers`, { teacher_ids: [tid] });
      toast.success(`${s.name} fixed — now visible to the teacher`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not fix"); } finally { setBusy(false); }
  };
  const linked = Object.entries(s.linked || {}).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(", ");
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-white p-2 text-sm" data-testid={`health-row-${s.id}`}>
      <span className="font-semibold">{s.name}</span>
      <span className="text-slate-500">{s.roll_number} · {s.student_class}</span>
      <span className="text-amber-800">{kind === "broken" ? `Main record was deleted after a merge${linked ? ` (${linked} waiting)` : ""}` : "No teacher assigned"}</span>
      <span className="ml-auto flex items-center gap-2">
        <TeacherPick teachers={teachers} value={tid} onChange={setTid} testid={`health-teacher-${s.id}`} />
        <Button size="sm" className="h-8" disabled={busy} onClick={fix} data-testid={`health-fix-${s.id}`}>
          {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}{kind === "broken" ? "Restore" : "Assign teacher"}
        </Button>
      </span>
    </li>
  );
}

export const RecordHealth = ({ onFixed }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/students/health").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const n = d.broken_merges.length + d.no_teacher.length;
  const done = () => { load(); onFixed?.(); };
  if (!n) return <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="record-health-ok"><CheckCircle2 className="h-4 w-4" /> Record health: every student is visible to a teacher.</p>;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="record-health">
      <p className="font-semibold text-amber-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Record health — {n} student(s) not visible to any teacher</p>
      <ul className="space-y-1.5">
        {d.broken_merges.map((s) => <Row key={s.id} s={s} kind="broken" teachers={d.teachers} onDone={done} />)}
        {d.no_teacher.map((s) => <Row key={s.id} s={s} kind="teacher" teachers={d.teachers} onDone={done} />)}
      </ul>
      <details className="text-xs text-amber-900" data-testid="record-health-help">
        <summary className="cursor-pointer font-semibold">How to fix this yourself in future</summary>
        <ul className="list-disc pl-5 mt-1 space-y-0.5">
          <li><b>Restore</b> brings back a student whose main record was deleted after a merge: same roll number, fees, reminders and attendance linked back. No new student is created.</li>
          <li><b>Assign teacher</b> makes a student appear in that teacher's login. The teacher shown first is the one from the student's old/merged record.</li>
          <li>Never delete a student who has fees or attendance — the app now blocks this, because their history would be left pointing at nothing. Keep the record and fix their details or teacher instead.</li>
          <li>When merging duplicates, keep the record that has the teacher; a merge with no teacher is refused.</li>
          <li>If a teacher says a child is missing: open this page, check this box first, then check the student's teacher in their profile.</li>
        </ul>
      </details>
    </div>
  );
};
